import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';

import {
  BREAKPOINTS,
  useMediaQuery,
} from '@skyscanner-internal/backpack-web/bpk-component-breakpoint';
import classNames from 'classnames';
import { GLOBAL } from 'saddlebag-browser';

import {
  setStickySearchControlsBounds,
  setStickySearchControlsHeight,
} from '../../hooks/useStickySearchControlsHeight';

import STYLES from './SearchControlsWrapper.module.scss';

const $window = GLOBAL.getWindow();

type StickySearchControlsProps = {
  children: ReactNode;
  className?: string;
  'data-testid'?: string;
};

/**
 * On desktop, fixes its children to the viewport once the user has scrolled
 * past it, while leaving everything else on the page (e.g. the hero
 * title/background rendered above it by SearchControlsWrapper) scrolling
 * normally. A placeholder keeps the page height stable so content below
 * doesn't jump when the fixed state toggles.
 */
const StickySearchControls = ({
  children,
  className = '',
  ...rest
}: StickySearchControlsProps) => {
  const isDesktop = useMediaQuery(BREAKPOINTS.ABOVE_TABLET);
  const placeholderRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const [isSticky, setSticky] = useState(false);
  const [placeholderHeight, setPlaceholderHeight] = useState<
    number | undefined
  >(undefined);

  useEffect(() => {
    // Measure with the fixed width, margins and padding, without remounting the
    // search form or waiting for the anchor scroll to activate the fixed state.
    const measureFixedHeight = () => {
      const container = containerRef.current;
      const inner = innerRef.current;
      if (!container || !inner) return 0;
      if (container.classList.contains(STYLES.StickySearchControls__fixed)) {
        return container.getBoundingClientRect().height;
      }

      container.classList.add(STYLES.StickySearchControls__fixed);
      inner.classList.add(STYLES.StickySearchControls__inner);
      try {
        return container.getBoundingClientRect().height;
      } finally {
        container.classList.remove(STYLES.StickySearchControls__fixed);
        inner.classList.remove(STYLES.StickySearchControls__inner);
      }
    };

    const updatePosition = () => {
      if (!placeholderRef.current || !containerRef.current) return;

      const { height, top } = placeholderRef.current.getBoundingClientRect();
      const shouldStick = isDesktop && top <= 0;
      setStickySearchControlsBounds(
        isDesktop
          ? { top: top + $window.pageYOffset, height: measureFixedHeight }
          : undefined,
      );
      // Keep the normal-flow footprint (including margins) when fixed sizing
      // changes. Otherwise a centred hero can move the activation threshold.
      if (
        !containerRef.current.classList.contains(
          STYLES.StickySearchControls__fixed,
        )
      ) {
        setPlaceholderHeight(height);
      }
      setSticky(shouldStick);
      setStickySearchControlsHeight(shouldStick ? measureFixedHeight() : 0);
    };

    updatePosition();
    // Search controls can change height after lazy loading or changing layout.
    const observer = new ResizeObserver(updatePosition);
    if (containerRef.current) observer.observe(containerRef.current);

    $window.addEventListener('scroll', updatePosition);
    $window.addEventListener('resize', updatePosition);

    return () => {
      observer.disconnect();
      $window.removeEventListener('scroll', updatePosition);
      $window.removeEventListener('resize', updatePosition);
      setStickySearchControlsHeight(0);
      setStickySearchControlsBounds(undefined);
    };
  }, [isDesktop]);

  return (
    <div
      ref={placeholderRef}
      className={STYLES.StickySearchControls__placeholder}
      style={isSticky ? { height: placeholderHeight } : undefined}
    >
      <div
        ref={containerRef}
        className={classNames({
          [STYLES.StickySearchControls__fixed]: isSticky,
        })}
      >
        <div
          ref={innerRef}
          className={classNames(className, {
            [STYLES.StickySearchControls__inner]: isSticky,
          })}
          {...rest}
        >
          {children}
        </div>
      </div>
    </div>
  );
};

export default StickySearchControls;
